import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootState } from '../store';
import { setUserRole } from '../store/userSlice';
import { UserRole } from '../types';
import { Colors, Spacing, FontSizes, BorderRadius, Shadows } from '../constants/theme';

interface UserRoleSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${Colors.overlayDark};
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const Modal = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  width: 100%;
  max-width: 500px;
  margin: ${Spacing.lg}px;
  overflow: hidden;
  box-shadow: ${Shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.border};
`;

const ModalTitle = styled.h2`
  font-size: ${FontSizes.xl}px;
  font-weight: 600;
  color: ${Colors.textPrimary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${Colors.textSecondary};
  font-size: ${FontSizes.xl}px;
  cursor: pointer;
  padding: ${Spacing.sm}px;
  
  &:hover {
    color: ${Colors.textPrimary};
  }
`;

const RolesContainer = styled.div`
  padding: ${Spacing.lg}px;
`;

const RoleCard = styled.div<{ isActive: boolean }>`
  padding: ${Spacing.lg}px;
  background-color: ${props => props.isActive ? Colors.surfaceHover : Colors.background};
  border-radius: ${BorderRadius.md}px;
  margin-bottom: ${Spacing.md}px;
  border: 2px solid ${props => props.isActive ? Colors.accent : Colors.border};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${Colors.borderLight};
    background-color: ${Colors.surfaceHover};
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const RoleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.sm}px;
`;

const RoleLabel = styled.h3<{ isActive: boolean }>`
  font-size: ${FontSizes.lg}px;
  font-weight: 600;
  color: ${props => props.isActive ? Colors.accent : Colors.textPrimary};
  margin: 0;
`;

const ActiveBadge = styled.span`
  background-color: ${Colors.accent};
  color: ${Colors.background};
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.xs}px;
  font-weight: 600;
`;

const RoleDescription = styled.p`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const Footer = styled.div`
  padding: ${Spacing.lg}px;
  border-top: 1px solid ${Colors.border};
  text-align: center;
`;

const FooterText = styled.p`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textTertiary};
  margin: 0;
`;

const UserRoleSelector: React.FC<UserRoleSelectorProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const currentRole = useSelector((state: RootState) => state.user.currentRole);

  const roles: Array<{ role: UserRole; label: string; description: string }> = [
    {
      role: 'HR_LEADER',
      label: 'HR Leader',
      description: 'Access full analytics, AI insights, and strategic planning tools',
    },
    {
      role: 'MANAGER',
      label: 'Manager',
      description: 'View team analytics, performance metrics, and development plans',
    },
    {
      role: 'RECRUITER',
      label: 'Recruiter',
      description: 'Manage requisitions, track candidates, and view recruitment analytics',
    },
  ];

  const handleRoleSelect = (role: UserRole) => {
    dispatch(setUserRole(role));
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay isOpen={isOpen} onClick={handleOverlayClick}>
      <Modal>
        <ModalHeader>
          <ModalTitle>Select User Role</ModalTitle>
          <CloseButton onClick={onClose}>✕</CloseButton>
        </ModalHeader>

        <RolesContainer>
          {roles.map((item) => (
            <RoleCard
              key={item.role}
              isActive={currentRole === item.role}
              onClick={() => handleRoleSelect(item.role)}
            >
              <RoleHeader>
                <RoleLabel isActive={currentRole === item.role}>
                  {item.label}
                </RoleLabel>
                {currentRole === item.role && (
                  <ActiveBadge>ACTIVE</ActiveBadge>
                )}
              </RoleHeader>
              <RoleDescription>{item.description}</RoleDescription>
            </RoleCard>
          ))}
        </RolesContainer>

        <Footer>
          <FooterText>
            Role determines available features and data visibility
          </FooterText>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default UserRoleSelector;